<script setup lang="ts">
	import axios from '@/lib/axios'
	import { useDebounceFn } from '@vueuse/core'
	import { computed, onMounted, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import type { User } from '@/modules/users/interfaces/user'

	type Program = { id: number; name: string }
	type Chain = { id: number; name: string; programs: Program[] }
	type NumberEntry = {
		id: number
		chainId: number
		chainName: string
		loyaltyProgramId: number
		loyaltyProgramName: string
		number: string
		isDefault: boolean
		serviceName: string
	}

	const props = defineProps<{ user: User }>()
	const toast = useToast()
	const { user: actor } = useAuthUser()
	const { hasPermissionUser } = useHasPermissionUser()
	const canManage = computed(
		() =>
			actor.value?.id === props.user.id ||
			hasPermissionUser('create-memberships', 'App\\Models\\User')
	)
	const enabled = ref(false)
	const policyLoading = ref(true)

	const entries = ref<NumberEntry[]>([])
	const chains = ref<Chain[]>([])
	const selectedChain = ref<Chain | null>(null)
	const programId = ref<number | null>(null)
	const number = ref('')
	const makeDefault = ref(false)
	const editingId = ref<number | null>(null)
	const loading = ref(true)
	const searching = ref(false)
	const saving = ref(false)
	const error = ref('')
	let searchVersion = 0

	const availablePrograms = computed(() => selectedChain.value?.programs ?? [])
	const filteredPrograms = computed(() =>
		availablePrograms.value.filter(
			(program) =>
				editingId.value ||
				!entries.value.some((entry) => entry.loyaltyProgramId === program.id)
		)
	)

	async function loadEntries() {
		loading.value = true
		error.value = ''
		try {
			const response = await axios.get<NumberEntry[]>(
				`/api/users/${props.user.id}/memberships`,
				{ errorHandling: false } as any
			)
			entries.value = response.data.filter(
				(entry) => entry.serviceName === 'Hotel'
			)
		} catch {
			error.value =
				'No se pudieron cargar los números de lealtad. Intenta de nuevo.'
		} finally {
			loading.value = false
		}
	}

	async function loadPolicy() {
		policyLoading.value = true
		try {
			const response = await axios.get<{ enabled: boolean }>(
				`/api/users/${props.user.id}/hotel-loyalty-policy`,
				{ errorHandling: false } as any
			)
			enabled.value = response.data.enabled
		} catch {
			error.value =
				'No se pudo consultar si la empresa permite números personales de hotel.'
		} finally {
			policyLoading.value = false
		}
	}

	async function searchChains(query: string) {
		const version = ++searchVersion
		searching.value = true
		try {
			const response = await axios.get<Chain[]>(
				'/api/hotel-chains/loyalty-catalog',
				{
					params: { query },
					errorHandling: false,
				} as any
			)
			if (version !== searchVersion) return
			chains.value = response.data
		} catch {
			if (version !== searchVersion) return
			error.value = 'No se pudo consultar el catálogo de cadenas.'
		} finally {
			if (version === searchVersion) searching.value = false
		}
	}

	const debouncedSearch = useDebounceFn(searchChains, 300)

	function resetForm() {
		editingId.value = null
		selectedChain.value = null
		programId.value = null
		number.value = ''
		makeDefault.value = false
		error.value = ''
	}

	function edit(entry: NumberEntry) {
		editingId.value = entry.id
		selectedChain.value = {
			id: entry.chainId,
			name: entry.chainName,
			programs: [
				{ id: entry.loyaltyProgramId, name: entry.loyaltyProgramName },
			],
		}
		programId.value = entry.loyaltyProgramId
		number.value = entry.number
		makeDefault.value = entry.isDefault
		error.value = ''
	}

	async function save() {
		if (!programId.value || !number.value.trim()) {
			error.value = 'Selecciona una cadena, un programa y escribe el número.'
			return
		}

		saving.value = true
		error.value = ''
		try {
			if (editingId.value) {
				await axios.put(
					`/api/memberships/${editingId.value}`,
					{
						number: number.value.trim(),
						isDefault: makeDefault.value,
					},
					{ errorHandling: false } as any
				)
			} else {
				await axios.post(
					`/api/users/${props.user.id}/memberships`,
					{
						loyaltyProgramId: programId.value,
						number: number.value.trim(),
					},
					{ errorHandling: false } as any
				)
			}
			toast.success('Número de lealtad guardado')
			resetForm()
			await loadEntries()
		} catch (cause: any) {
			error.value =
				cause.response?.data?.message ??
				'No se pudo guardar el número. Revisa los datos e intenta de nuevo.'
		} finally {
			saving.value = false
		}
	}

	async function setDefault(entry: NumberEntry) {
		try {
			await axios.put(`/api/memberships/${entry.id}`, { isDefault: true }, {
				errorHandling: false,
			} as any)
			await loadEntries()
			toast.success('Número predeterminado actualizado')
		} catch {
			error.value = 'No se pudo cambiar el número predeterminado.'
		}
	}

	async function remove(entry: NumberEntry) {
		if (
			!window.confirm(
				`¿Eliminar el número de ${entry.loyaltyProgramName}? Las reservas anteriores conservarán el número que usaron.`
			)
		)
			return
		try {
			await axios.delete(`/api/memberships/${entry.id}`, {
				errorHandling: false,
			} as any)
			await loadEntries()
			toast.success('Número eliminado')
		} catch {
			error.value = 'No se pudo eliminar el número.'
		}
	}

	onMounted(() => {
		loadEntries()
		loadPolicy()
		searchChains('')
	})
	watch(
		() => props.user.id,
		() => {
			resetForm()
			loadEntries()
			loadPolicy()
		}
	)
	watch(selectedChain, () => {
		if (!editingId.value) programId.value = null
	})
</script>

<template>
	<section class="space-y-5">
		<div class="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
			<h2 class="text-lg font-semibold text-gray-900">
				Programas de lealtad del viajero
			</h2>
			<p class="mt-2 text-sm text-gray-600">
				Busca la cadena, elige uno de sus programas y guarda el número del
				viajero. Cada viajero tiene sus propios números, independientes de los
				de la empresa.
			</p>
		</div>

		<p
			v-if="error"
			role="alert"
			class="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
		>
			{{ error }}
		</p>

		<p
			v-if="policyLoading"
			class="text-sm text-gray-600"
		>
			Consultando configuración de la empresa...
		</p>
		<p
			v-else-if="!enabled"
			class="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
		>
			La empresa aún no habilita números personales de hotel. Un administrador
			puede activarlos en los programas de lealtad de la empresa.
		</p>

		<form
			v-if="enabled && canManage"
			class="grid gap-4 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 md:grid-cols-2"
			@submit.prevent="save"
		>
			<div class="min-w-0">
				<StyledCombobox
					v-model="selectedChain"
					:options="chains"
					:display-value="(chain: Chain) => chain.name"
					:on-input-change="debouncedSearch"
					:loading-options="searching"
					label="Cadena hotelera"
					placeholder="Busca una cadena..."
					:disabled="!!editingId"
				>
					<template #default="{ option }">{{ option.name }}</template>
				</StyledCombobox>
			</div>
			<div class="min-w-0">
				<StyledSelect
					v-model="programId"
					label="Programa de la cadena"
					:options="
						filteredPrograms.map((program) => ({
							label: program.name,
							value: program.id,
						}))
					"
					:disabled="!selectedChain || !!editingId"
					placeholder="Selecciona un programa"
				/>
				<p
					v-if="selectedChain && !availablePrograms.length"
					class="mt-1 text-sm text-gray-600"
				>
					Esta cadena aún no tiene programas registrados. Solicita el alta al
					equipo de Enlace.
				</p>
			</div>
			<div class="min-w-0">
				<label
					for="traveler-loyalty-number"
					class="mb-1 block text-sm font-medium text-gray-700"
					>Número de lealtad</label
				>
				<input
					id="traveler-loyalty-number"
					v-model="number"
					type="text"
					maxlength="64"
					autocomplete="off"
					required
					class="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
					placeholder="Tal como aparece en la cuenta"
				/>
				<p class="mt-1 text-xs text-gray-500">
					Puede contener letras, números, espacios o guiones.
				</p>
			</div>
			<label
				v-if="editingId"
				class="flex items-center gap-2 self-center text-sm text-gray-700"
			>
				<input
					v-model="makeDefault"
					type="checkbox"
					:disabled="
						!!editingId &&
						entries.some((entry) => entry.id === editingId && entry.isDefault)
					"
					class="h-4 w-4 rounded border-gray-300 disabled:opacity-60"
				/>
				Usar por defecto en esta cadena
			</label>
			<p
				v-else
				class="self-center text-xs text-gray-600"
			>
				El primer número de cada cadena se marca como predeterminado. Puedes
				cambiarlo después.
			</p>
			<div class="flex flex-wrap gap-2 md:col-span-2">
				<button
					type="submit"
					:disabled="saving"
					class="rounded-lg bg-[#253C70] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
				>
					{{
						saving ? 'Guardando...'
						: editingId ? 'Guardar cambio'
						: 'Agregar número'
					}}
				</button>
				<button
					v-if="editingId"
					type="button"
					class="rounded-lg border px-4 py-2 text-sm"
					@click="resetForm"
				>
					Cancelar
				</button>
			</div>
		</form>

		<div class="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
			<h3 class="font-semibold text-gray-900">Números registrados</h3>
			<p
				v-if="loading"
				class="mt-3 text-sm text-gray-500"
			>
				Cargando números...
			</p>
			<p
				v-else-if="!entries.length"
				class="mt-3 text-sm text-gray-500"
			>
				Este viajero aún no tiene números de lealtad registrados.
			</p>
			<ul
				v-else
				class="mt-3 divide-y divide-gray-100"
			>
				<li
					v-for="entry in entries"
					:key="entry.id"
					class="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
				>
					<div class="min-w-0">
						<p class="break-words font-medium text-gray-900">
							{{ entry.chainName }} · {{ entry.loyaltyProgramName }}
						</p>
						<p class="mt-1 text-sm text-gray-600">
							{{ entry.number }}
							<span
								v-if="entry.isDefault"
								class="ml-1 rounded bg-blue-50 px-2 py-0.5 text-blue-700"
								>Predeterminado</span
							>
						</p>
					</div>
					<div
						v-if="enabled && canManage"
						class="flex flex-wrap gap-2"
					>
						<button
							v-if="!entry.isDefault"
							type="button"
							class="rounded-lg border px-3 py-2 text-sm"
							@click="setDefault(entry)"
						>
							Usar por defecto
						</button>
						<button
							type="button"
							class="rounded-lg border px-3 py-2 text-sm"
							@click="edit(entry)"
						>
							Editar
						</button>
						<button
							type="button"
							class="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700"
							@click="remove(entry)"
						>
							Eliminar
						</button>
					</div>
				</li>
			</ul>
		</div>
	</section>
</template>
